import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { CircularProgress, InputAdornment, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

// UTILITY
import {
  analyticsChooseSearchItem,
  analyticsLeftSearch,
  analyticsNoSearchResults,
  analyticsSearched,
} from '@/utility/analytics';

import { AutocompleteWrapper, SearchItem, NoResults } from './styles';

const AutocompleteF = React.forwardRef((props, ref) => {
  return <Autocomplete {...props} ref={ref} />;
});

const SearchChannels = props => {
  const { loading, inputProps, innerRef, analyticsLocation, ...restProps } = props;

  const [t] = useTranslation();

  const [open, setOpen] = React.useState(false);
  const itemWasSelected = React.useRef(false);

  useEffect(() => {
    if (restProps.options?.length) {
      const properties = {
        query: restProps.inputValue,
        numberOfItemsDisplayed: restProps.options?.length,
        first_item_type: restProps.options[0].type,
        first_item_name: restProps.options[0].name,
        first_item_id: restProps.options[0].channel_id,
        search_location: analyticsLocation,
      };

      analyticsSearched(properties);
    }
  }, [restProps.options]);

  const isNoResults =
    open && !loading && !restProps.options?.length && !!restProps.inputValue.length;

  useEffect(() => {
    if (isNoResults) {
      analyticsNoSearchResults({ query: restProps.inputValue, search_location: analyticsLocation });
    }
  }, [restProps.options, loading]);

  const handleOpen = state => {
    setOpen(state);
  };

  const onOpen = e => {
    itemWasSelected.current = false;
    handleOpen(true);
    restProps.onOpen(e);
  };

  const handleClose = (e, reason) => {
    if (e?.keyCode === 13 && (reason === 'reset' || reason === 'create-option')) {
      return;
    }

    if (reason === 'blur' && !itemWasSelected.current) {
      const properties = {
        query: restProps.inputValue,
        numberOfResults: restProps.options?.length,
        search_location: analyticsLocation,
      };

      analyticsLeftSearch(properties);

      if (itemWasSelected.current) itemWasSelected.current = false;
    }

    handleOpen(false);
    restProps.onClose(e, reason);
  };

  const handleChange = (event, value, reason) => {
    if (reason === 'select-option') {
      itemWasSelected.current = true;
    } else if (reason === 'clear') {
      itemWasSelected.current = false;
    }

    const index = restProps.options.findIndex(option => option.channel_id === value.channel_id);

    const properties = {
      query: restProps.inputValue,
      numberOfItemsDisplayed: restProps.options?.length,
      type: value.type,
      title: value.name,
      id: value.channel_id,
      positionOfItem: index + 1,
      search_location: analyticsLocation,
    };

    analyticsChooseSearchItem(properties);
    restProps.onChange(event, value, reason, handleOpen);
  };

  const getTypeContent = optData => {
    if (optData.type === 'show' || optData.type === 'sport_team') {
      return (
        <div className="search-item__type-image">
          <img src={optData.img} alt="" />
        </div>
      );
    }

    if (optData.type === 'channel_bundle') {
      return null;
    }

    return <span className="search-item__type">{optData.type}</span>;
  };

  return (
    <AutocompleteWrapper>
      <AutocompleteF
        open={open}
        {...restProps}
        onChange={handleChange}
        ref={innerRef}
        onOpen={onOpen}
        onClose={handleClose}
        blurOnSelect
        getOptionLabel={option => option.show_name || ''}
        filterOptions={options => {
          return options;
        }}
        loading={loading}
        forcePopupIcon={false}
        disableCloseOnSelect
        renderOption={option => (
          <SearchItem className="search-item">
            <div className="search-item__main-info">
              <div className="search-item__image">
                <img src={option.show_img} alt="" />
              </div>
              <div className="search-item__title">{option.show_name}</div>
            </div>
            {getTypeContent(option)}
          </SearchItem>
        )}
        renderInput={params => (
          <TextField
            {...params}
            {...inputProps}
            placeholder={t('inputs.findChannels')}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment className="text-color--default" position="start">
                  <i className="icon-search" />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress className="loading" size={16} /> : null}{' '}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        disablePortal
      />
      {isNoResults && <NoResults>{t('searchAutoComplete.noResults')}</NoResults>}
    </AutocompleteWrapper>
  );
};

SearchChannels.defaultProps = {
  loading: false,
  onOpen: () => {},
  onClose: () => {},
  inputProps: {},
  innerRef: {},
  analyticsLocation: '',
};

SearchChannels.propTypes = {
  loading: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  inputProps: PropTypes.object,
  innerRef: PropTypes.object,
  analyticsLocation: PropTypes.string,
};

export default React.memo(SearchChannels);
